<template>
  <fullscreen-dialog
    v-model="model"
    :title="user ? toLatin(user.user_name) : ''"
    :subtitle="`ID: ${user ? user.user_id : ''}`"
    hide-footer
  >
    <template #default>
      <v-card-text>
        <div v-if="!user">
          {{ $t("no_data") }}
        </div>

        <div v-else>
          <!-- Summary Card -->
          <v-card outlined class="mb-4">
            <v-card-text>
              <v-row dense justify="space-between">
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">{{ $t("orders") }}</div>
                  <div class="text-h6 primary--text">
                    {{ filteredTransactions.length }}
                  </div>
                </v-col>
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">
                    {{ $t("orders_total") }}
                  </div>
                  <div class="text-h6">{{ formatAmount(filteredTotal) }}</div>
                </v-col>
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">
                    {{ $t("spent_on_credits") }}
                  </div>
                  <div class="text-h6 error--text">
                    {{ formatAmount(user.totals && user.totals.spent_on_credits) }}
                  </div>
                </v-col>
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">
                    {{ $t("credit_balance") }}
                  </div>
                  <div class="text-h6 success--text">
                    {{ formatAmount(user.totals && user.totals.current_credit_balance) }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Filters row -->
          <v-row align="center" class="mb-2">
            <v-col cols="auto">
              <month-picker v-model="selectedMonth" color="white"></month-picker>
            </v-col>
            <v-col cols="12" sm="5" md="4">
              <autocomplete
                v-model="selectedRestaurantIds"
                :items="companyRestaurants"
                :item-text="(item) => toLatin(item.name)"
                item-value="id"
                :label="$t('restaurant')"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
                :hide-details="false"
                color="white"
              />
            </v-col>
            <v-col cols="3" md="2">
              <text-field
                v-model="search"
                kind="search"
                :label="$t('search')"
                clearable
                color="white"
              ></text-field>
            </v-col>
            <v-col align="end">
              <v-row no-gutters align="center" justify="end">
                <excel-button class="mr-2" @click="exportToExcel"></excel-button>
                <print-button @click="printReport"></print-button>
              </v-row>
            </v-col>
          </v-row>

          <!-- Transactions Table -->
          <v-data-table
            :headers="headers"
            :items="filteredTransactionsWithTotal"
            hide-default-footer
            disable-pagination
            class="striped-table ordera-box-shadow"
          >
            <template v-slot:item.num="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold' : ''">{{ item.num }}</span>
            </template>

            <template v-slot:item.date="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold' : ''">
                {{ item.isTotal ? $t("total").toUpperCase() : localeDate(item.date) }}
              </span>
            </template>

            <template v-slot:item.amount="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold primary--text' : ''">
                {{ formatAmount(item.amount) }}
              </span>
            </template>

            <template v-slot:item.meal_name="{ item }">
              <span v-if="!item.isTotal">{{ toLatin(item.meal_name) }}</span>
            </template>

            <template v-slot:item.notes="{ item }">
              <span v-if="!item.isTotal" class="text--secondary">{{ item.notes }}</span>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </template>
  </fullscreen-dialog>
</template>

<script>
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import hasModel from "@/mixins/hasModel";
import hasExcel from "@/mixins/hasExcel";
import hasPrinting from "@/mixins/hasPrinting";

export default {
  components: {
    FullscreenDialog,
  },
  mixins: [hasModel, hasExcel, hasPrinting],
  props: {
    user: {
      type: Object,
      default: null,
    },
    companyId: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    selectedMonth: new Date().toISOString().substr(0, 7),
    search: "",
    selectedRestaurantIds: [],
  }),
  computed: {
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false, width: "80px" },
        { text: this.$t("date"), value: "date", sortable: true },
        { text: this.$t("meal"), value: "meal_name", sortable: false },
        { text: this.$t("amount"), value: "amount", sortable: false, align: "right" },
        { text: this.$t("note"), value: "notes", sortable: false },
      ];
    },
    monthName() {
      return this.localeDate(this.selectedMonth + "-01", {
        month: "long",
        year: "numeric",
      });
    },
    companyRestaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    allTransactions() {
      if (!this.user?.orderTransactions?.length) return [];
      let source = [...this.user.orderTransactions];
      const sel = this.selectedRestaurantIds;
      if (sel.length > 0 && sel.length < this.companyRestaurants.length) {
        source = source.filter((t) => sel.includes(t.restaurant_id));
      }
      return source
        .sort(
          (a, b) => new Date(a.date || a.created_at) - new Date(b.date || b.created_at)
        )
        .map((t, index) => ({
          id: t.id,
          num: index + 1,
          date: t.date || t.created_at || null,
          meal_name: t.orderable?.name || "",
          amount: this.parseAmount(t.total_amount ?? t.price),
          notes: t.notes || "",
          isTotal: false,
        }));
    },
    filteredTransactions() {
      let txs = this.allTransactions;

      if (this.selectedMonth) {
        const [year, month] = this.selectedMonth.split("-").map(Number);
        txs = txs.filter((t) => {
          if (!t.date) return false;
          const d = new Date(t.date);
          return d.getFullYear() === year && d.getMonth() + 1 === month;
        });
      }

      if (this.search) {
        const q = this.search.toLowerCase();
        txs = txs.filter(
          (t) =>
            (t.notes || "").toLowerCase().includes(q) ||
            (t.meal_name || "").toLowerCase().includes(q) ||
            String(t.id || "").includes(q) ||
            this.localeDate(t.date).toLowerCase().includes(q)
        );
      }

      return txs.map((t, i) => ({ ...t, num: i + 1 }));
    },
    filteredTransactionsWithTotal() {
      const txs = [...this.filteredTransactions];
      if (txs.length > 0) {
        txs.push({
          id: "total",
          num: "",
          date: "",
          amount: this.filteredTotal,
          notes: "",
          isTotal: true,
        });
      }
      return txs;
    },
    filteredTotal() {
      return this.filteredTransactions.reduce((sum, t) => sum + t.amount, 0);
    },
  },
  created() {
    this.selectedRestaurantIds = this.companyRestaurants.map((r) => r.id);
  },
  watch: {
    user() {
      this.selectedRestaurantIds = this.companyRestaurants.map((r) => r.id);
    },
    model(val) {
      if (val) {
        this.selectedRestaurantIds = this.companyRestaurants.map((r) => r.id);
      }
    },
    companyRestaurants(val) {
      if (this.selectedRestaurantIds.length === 0 && val.length > 0) {
        this.selectedRestaurantIds = val.map((r) => r.id);
      }
    },
  },
  methods: {
    parseAmount(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    formatAmount(amount) {
      if (amount === null || amount === undefined || amount === "") return "0.00";
      return (parseFloat(amount) || 0).toFixed(2) + " " + (this.restaurantCurrency || "");
    },
    exportToExcel() {
      const exportItems = this.filteredTransactions.map((item) => ({
        num: item.num,
        date: this.localeDate(item.date),
        meal_name: this.toLatin(item.meal_name),
        amount: (item.amount || 0).toFixed(2),
        notes: item.notes,
      }));
      const totalRow = {
        num: "",
        date: this.$t("total"),
        meal_name: "",
        amount: this.filteredTotal.toFixed(2),
        notes: "",
      };
      this.downloadExcel({
        fileName: `${this.user.user_name}_${this.monthName}.xlsx`,
        sheets: [
          {
            name: this.user.user_name,
            headers: this.headers,
            items: [...exportItems, totalRow],
            title: this.user.user_name,
            subtitle: this.monthName,
          },
        ],
      });
    },
    printReport() {
      const printItems = this.filteredTransactions.map((item) => ({
        num: item.num,
        date: this.localeDate(item.date),
        meal_name: this.toLatin(item.meal_name),
        amount: (item.amount || 0).toFixed(2),
        notes: item.notes,
      }));
      const totalRow = {
        num: "",
        date: this.$t("total"),
        meal_name: "",
        amount: this.filteredTotal.toFixed(2),
        notes: "",
      };
      this.printDocument({
        title: this.user.user_name,
        subtitle: this.monthName,
        tables: [
          {
            headers: this.headers,
            items: [...printItems, totalRow],
          },
        ],
      });
    },
  },
};
</script>
